import {
  DEFAULT_APEX_PROFILES,
  type CmmdProviderAccountInventoryAccount,
  getProviderCatalogModel,
  type CmmdOrgProviderPolicy,
  type ProviderKind,
  type ServerProvider,
} from "@cmmd-forge/contracts";
import { memo, useMemo, useState } from "react";
import type { VariantProps } from "class-variance-authority";

import { type ProviderPickerKind, PROVIDER_OPTIONS } from "../../session-logic";
import { buttonVariants } from "../ui/button";
import { ComposerControl, ComposerControlChevron, ComposerControlIcon } from "./ComposerControl";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";
import { Skeleton } from "../ui/skeleton";
import { cn } from "~/lib/utils";

import { ModelPickerContent } from "./ModelPickerContent";
import {
  PICKER_PROVIDER_ACCENT_CLASS_BY_PROVIDER,
  PICKER_PROVIDER_ICON_BY_PROVIDER,
  type ModelPickerRowModel,
} from "./ModelListRow";
import { isProviderAllowedByExecutionPolicy } from "../../providerPickerAvailability";
import { resolveTriggerLabel } from "./providerModelPickerLabels";
import type { ModelPickerProviderAvailability } from "./modelPickerProviderAvailability";
import { composerFloatingLayerProps } from "./composerEventScope";

/**
 * Hidden picker kinds are internal/compatibility providers. Apex remains a
 * ProviderKind for old sessions, but user-facing selection is CMMD provider +
 * Apex profile models.
 */
const HIDDEN_PROVIDER_PICKER_KINDS = new Set<ProviderPickerKind>(["ollama", "apex"]);

export const isVisibleProviderPickerKind = (provider: ProviderKind): boolean =>
  !HIDDEN_PROVIDER_PICKER_KINDS.has(provider);

// Raw upstream model slugs that should never appear in provider rows. Apex
// profiles deliberately wrap these — DeepSeek/Kimi backends are
// addressable only through the four branded Apex profiles, not directly.
const HIDDEN_MODEL_SLUG_PREFIXES = ["kimi-", "deepseek-", "moonshot-"] as const;
export const isVisibleProviderModelSlug = (slug: string): boolean =>
  !HIDDEN_MODEL_SLUG_PREFIXES.some((prefix) => slug.toLowerCase().startsWith(prefix));
const isHiddenModelSlug = (slug: string): boolean => !isVisibleProviderModelSlug(slug);

function visibleModelOptions(
  provider: ProviderKind,
  options: ReadonlyArray<ModelPickerRowModel>,
): ReadonlyArray<ModelPickerRowModel> {
  return options.filter((option) => !isHiddenModelSlug(option.slug));
}

function providerIconClassName(
  provider: ProviderKind | ProviderPickerKind,
  fallback: string,
): string {
  return PICKER_PROVIDER_ACCENT_CLASS_BY_PROVIDER[provider as ProviderKind] ?? fallback;
}

function displayModelLabel(option: ModelPickerRowModel, provider: ProviderKind): string {
  if (provider === "codex") {
    return getProviderCatalogModel("codex", option.slug)?.name ?? option.name;
  }
  if (provider !== "apex" && provider !== "cmmd") return option.name;
  const apexProfile = DEFAULT_APEX_PROFILES.find((profile) => profile.slug === option.slug);
  if (apexProfile) return apexProfile.displayName ?? apexProfile.slug;
  return option.name;
}

function isVisibleAvailableProviderOption(
  option: (typeof PROVIDER_OPTIONS)[number],
  policy?: CmmdOrgProviderPolicy | null,
) {
  return (
    option.available &&
    isVisibleProviderPickerKind(option.value) &&
    isProviderAllowedByExecutionPolicy(option.value, policy)
  );
}

/**
 * Picker options that the existing composer code expects via this export.
 * Kept as a stable list so non-picker callers (NewThreadLanding, etc.)
 * continue to reach the available providers; the picker itself derives the
 * sidebar from `props.providers` snapshots.
 */
export function getAvailableProviderOptions(policy?: CmmdOrgProviderPolicy | null) {
  return PROVIDER_OPTIONS.filter((option) => isVisibleAvailableProviderOption(option, policy));
}

export const ProviderModelPicker = memo(function ProviderModelPicker(props: {
  provider: ProviderKind;
  model: string;
  lockedProvider: ProviderKind | null;
  providers?: ReadonlyArray<ServerProvider>;
  /** Personal provider accounts available for a new-thread account choice. */
  readonly providerAccounts?: ReadonlyArray<CmmdProviderAccountInventoryAccount>;
  /**
   * Why this picker may withhold each provider it shows, one entry per rail
   * row. Required so a call site chooses on purpose: a surface that can
   * reach CMMD's account authority passes the real join, and one with no
   * Environment in scope passes `PROVIDER_AVAILABILITY_PAYLOAD_ONLY`. Omitting
   * this used to fall back silently to what the payload alone could prove,
   * which is how a credential still being delivered read differently on an
   * open thread than on `/new` for the same reason.
   */
  readonly providerAvailability: ReadonlyArray<ModelPickerProviderAvailability>;
  readonly selectedProviderAccountId?: string;
  readonly onProviderAccountChange?: (provider: "codex" | "claudeAgent", accountId: string) => void;
  readonly modelOptionsByProvider: Record<ProviderKind, ReadonlyArray<ModelPickerRowModel>>;
  activeProviderIconClassName?: string;
  compact?: boolean;
  /**
   * Trigger text to show when no model label can be resolved from `model` +
   * `modelOptionsByProvider`, but a model *will* be resolved later (e.g. the
   * /new cloud surface, where a stopped runtime has no provider inventory yet
   * and the model is picked when the VM connects on submit). Without this the
   * trigger falls back to a loading skeleton, which never resolves on those
   * surfaces and reads as a broken control. Omit it for genuinely-loading
   * surfaces so the skeleton still communicates "config is arriving".
   */
  readonly unresolvedLabel?: string | undefined;
  /** Exact CMMD policy in cloud mode, `null` for standalone/runtime authority. */
  readonly orgProviderPolicy?: CmmdOrgProviderPolicy | null;
  /**
   * Whether a target project is selected. Pass ONLY from surfaces that can
   * select one (`/new`); omitting it means "no project concept here", which is
   * why an open thread must not pass `false`.
   */
  readonly projectSelected?: boolean;
  /**
   * When this picker's providers came from a stopped Environment's
   * last-known catalog, the moment that Environment published it. Set the
   * header line naming how old the catalog is; omitted or `null` renders
   * nothing, which is the live-inventory case.
   */
  readonly lastKnownInventoryPublishedAt?: string | null;
  triggerVariant?: VariantProps<typeof buttonVariants>["variant"];
  /**
   * Refuse the choice outright rather than take one that cannot be saved.
   *
   * Set by surfaces whose write has no target — a project group with no
   * connected checkout plans zero updates, so an enabled picker would accept
   * the selection and discard it in silence.
   */
  disabled?: boolean;
  triggerClassName?: string;
  onProviderModelChange: (provider: ProviderKind, model: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const policy = props.orgProviderPolicy;
  const requestedActiveProvider = props.lockedProvider ?? props.provider;
  const activeProvider =
    props.lockedProvider ??
    (isProviderAllowedByExecutionPolicy(requestedActiveProvider, policy)
      ? requestedActiveProvider
      : "codex");
  const displayProvider =
    props.provider === "cmmd" || props.provider === "apex" ? "cmmd" : activeProvider;
  const ProviderIcon = PICKER_PROVIDER_ICON_BY_PROVIDER[displayProvider];

  // Filter Apex upstream slugs out of every provider's model list except
  // Apex itself, then map into the picker's row-model shape.
  const filteredOptionsByProvider = useMemo<
    Record<ProviderKind, ReadonlyArray<ModelPickerRowModel>>
  >(() => {
    const out = {} as Record<ProviderKind, ReadonlyArray<ModelPickerRowModel>>;
    for (const provider of Object.keys(props.modelOptionsByProvider) as ProviderKind[]) {
      if (!isVisibleProviderPickerKind(provider)) continue;
      if (!isProviderAllowedByExecutionPolicy(provider, policy)) continue;
      const options = props.modelOptionsByProvider[provider];
      out[provider] = visibleModelOptions(provider, options).map((option) => ({
        slug: option.slug,
        isDefault: option.isDefault,
        isLegacy: option.isLegacy,
        name: displayModelLabel(option, provider),
      }));
    }
    return out;
  }, [policy, props.modelOptionsByProvider]);

  const triggerLabel = resolveTriggerLabel({
    options: filteredOptionsByProvider[displayProvider],
    model: props.model,
    provider: displayProvider,
    unresolvedLabel: props.unresolvedLabel,
  });

  const handleProviderModelChange = (provider: ProviderKind, slug: string) => {
    props.onProviderModelChange(provider, slug);
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        render={
          <ComposerControl
            variant={props.triggerVariant ?? "ghost"}
            disabled={props.disabled ?? false}
            data-chat-provider-model-picker="true"
            className={cn(
              // The one control that stays a half-step louder than its peers:
              // it names what is about to answer you.
              "justify-start overflow-hidden font-medium text-foreground/80",
              // Compact and below `md` the picker gives way rather than pushing
              // the overflow trigger out of the scrolling dock: its label
              // already truncates, and a model name is recoverable from the
              // menu while a control scrolled off the row is not.
              props.compact
                ? "min-w-touch max-w-42 shrink md:shrink-0"
                : "max-w-48 shrink sm:max-w-56",
              props.triggerClassName,
            )}
          />
        }
      >
        <span
          className={cn(
            "flex min-w-0 w-full box-border items-center gap-1.5 overflow-hidden",
            props.compact ? "max-w-36 sm:pl-1" : undefined,
          )}
        >
          {ProviderIcon ? (
            <ComposerControlIcon
              icon={ProviderIcon}
              className={cn(
                providerIconClassName(displayProvider, "text-muted-foreground/70"),
                props.activeProviderIconClassName,
              )}
            />
          ) : null}
          {triggerLabel ? (
            <span className="min-w-0 flex-1 truncate">{triggerLabel}</span>
          ) : (
            <Skeleton
              aria-label="Loading model"
              className="h-3.5 min-w-16 flex-1 rounded-full bg-muted-foreground/15"
            />
          )}
          <ComposerControlChevron />
        </span>
      </PopoverTrigger>
      <PopoverPopup
        {...composerFloatingLayerProps}
        align="start"
        className="border-0 bg-transparent p-0 shadow-none before:hidden [--viewport-inline-padding:0] *:data-[slot=popover-viewport]:p-0"
      >
        <ModelPickerContent
          {...props}
          activeProvider={activeProvider}
          modelOptionsByProvider={filteredOptionsByProvider}
          onRequestClose={() => setIsOpen(false)}
          onProviderModelChange={handleProviderModelChange}
        />
      </PopoverPopup>
    </Popover>
  );
});
